import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Box,
  Chip,
  Divider,
  Grid
} from '@mui/material';
import {
  AccountBalanceWallet,
  Business,
  Person,
  Settings,
  CheckCircle,
  Cancel
} from '@mui/icons-material';
import { useProfile } from '../../context/userContext';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuth } from '../../context/authContext';
import {
  getSubscriptionSummary,
  openIndividualPortal,
  openOrganizationPortal
} from '../../network/apis/subscription.api';
import styles from '../../styles/SubscriptionDashboard.module.css';

const SubscriptionDashboard = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingPortal, setProcessingPortal] = useState(null);
  const { profile } = useProfile();
  const { user: auth0User } = useAuth0();
  const { token } = useAuth();

  useEffect(() => {
    const fetchSummary = async () => {
      const userId = profile?.userId || auth0User?.sub;
      if (!userId || !token) {
        setError('User information not available');
        setLoading(false);
        return;
      }

      try {
        const data = await getSubscriptionSummary(token, userId);
        setSummary(data);
        setLoading(false);
      } catch (err) {
        setError('Failed to load subscription information. Please try again.');
        setLoading(false);
      }
    };

    fetchSummary();
  }, [profile, auth0User, token]);

  const handleManageIndividual = async () => {
    const customerId = profile?.stripeCustomerId;
    if (!customerId) {
      setError('Stripe customer ID not found. Please contact support.');
      return;
    }

    setProcessingPortal('individual');

    try {
      const { url } = await openIndividualPortal(token, customerId);
      window.location.href = url;
    } catch (err) {
      setError('Failed to open billing portal. Please try again.');
      setProcessingPortal(null);
    }
  };

  const handleManageOrganization = async (orgUuid) => {
    setProcessingPortal(orgUuid);

    try {
      const { url } = await openOrganizationPortal(token, orgUuid);
      window.location.href = url;
    } catch (err) {
      setError('Failed to open billing portal. Please try again.');
      setProcessingPortal(null);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'success';
      case 'trialing':
        return 'info';
      case 'past_due':
        return 'warning';
      case 'canceled':
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box className={styles['dashboard-container']}>
        <Box className={styles['loading-container']}>
          <CircularProgress size={50} />
          <p className={styles['loading-text']}>Loading your subscriptions...</p>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={styles['dashboard-container']}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!summary) {
    return (
      <Box className={styles['dashboard-container']}>
        <Alert severity="info">
          No subscription information available.
        </Alert>
      </Box>
    );
  }

  return (
    <Box className={styles['dashboard-container']}>
      {/* Page Header */}
      <div className={styles['dashboard-header']}>
        <h1 className={styles['dashboard-title']}>
          <AccountBalanceWallet className={styles['title-icon']} />
          My Subscriptions
        </h1>
        <p className={styles['dashboard-subtitle']}>
          Manage your individual and organization subscriptions
        </p>
      </div>

      {/* Dual Subscription Alert */}
      {summary.hasDualSubscriptions && (
        <Alert severity="info" className={styles['dual-alert']}>
          <strong>You have multiple active subscriptions</strong>
          <div style={{ marginTop: '8px', fontSize: '0.9rem' }}>
            Total Monthly Cost: <strong>${Number(summary.totalMonthlyCost || 0).toFixed(2)}</strong>
          </div>
        </Alert>
      )}

      {/* Subscriptions Grid */}
      <Grid container spacing={3} className={styles['subscriptions-grid']}>
        {/* Individual Subscription Card */}
        {summary.individualSubscription && (
          <Grid item xs={12} md={summary.organizationSubscriptions.length > 0 ? 6 : 12}>
            <Card className={styles['subscription-card']} elevation={3}>
              <CardContent>
                {/* Card Header */}
                <div className={styles['card-header']}>
                  <div className={styles['card-title-section']}>
                    <Person className={styles['card-icon']} color="primary" />
                    <div>
                      <h3 className={styles['card-title']}>Individual Subscription</h3>
                      <p className={styles['card-subtitle']}>Personal workspace</p>
                    </div>
                  </div>
                  <Chip
                    label={summary.individualSubscription.status || 'Active'}
                    color={getStatusColor(summary.individualSubscription.status)}
                    size="small"
                  />
                </div>

                <Divider className={styles['card-divider']} />

                {/* Plan Details */}
                <div className={styles['plan-details']}>
                  <h4 className={styles['plan-name']}>
                    {summary.individualSubscription.planName}
                  </h4>

                  <div className={styles['detail-row']}>
                    <span className={styles['detail-label']}>Next Renewal:</span>
                    <span className={styles['detail-value']}>
                      {formatDate(summary.individualSubscription.nextRenewalDate)}
                    </span>
                  </div>

                  <div className={styles['detail-row']}>
                    <span className={styles['detail-label']}>Subscription ID:</span>
                    <span className={styles['detail-value-small']}>
                      {summary.individualSubscription.stripeSubscriptionId?.substring(0, 20)}...
                    </span>
                  </div>
                </div>

                <Divider className={styles['card-divider']} />

                {/* Features */}
                <div className={styles['features-section']}>
                  <h5 className={styles['features-title']}>Features</h5>
                  <div className={styles['features-list']}>
                    <div className={styles['feature-item']}>
                      <CheckCircle className={styles['feature-icon-check']} fontSize="small" />
                      <span>Storage: {summary.individualSubscription.features.storage}</span>
                    </div>
                    <div className={styles['feature-item']}>
                      {summary.individualSubscription.features.fleetManagement ? (
                        <CheckCircle className={styles['feature-icon-check']} fontSize="small" />
                      ) : (
                        <Cancel className={styles['feature-icon-cancel']} fontSize="small" />
                      )}
                      <span>Fleet Management</span>
                    </div>
                    <div className={styles['feature-item']}>
                      <CheckCircle className={styles['feature-icon-check']} fontSize="small" />
                      <span>Drones: {summary.individualSubscription.features.drones}</span>
                    </div>
                    <div className={styles['feature-item']}>
                      <CheckCircle className={styles['feature-icon-check']} fontSize="small" />
                      <span>Batteries: {summary.individualSubscription.features.batteries}</span>
                    </div>
                  </div>
                </div>

                {/* Manage Button */}
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  startIcon={<Settings />}
                  onClick={handleManageIndividual}
                  disabled={processingPortal === 'individual'}
                  className={styles['manage-button']}
                >
                  {processingPortal === 'individual' ? 'Opening...' : 'Manage Subscription'}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Organization Subscription Cards */}
        {summary.organizationSubscriptions.map((org) => (
          <Grid item xs={12} md={summary.individualSubscription ? 6 : 12} key={org.organizationUuid}>
            <Card className={styles['subscription-card']} elevation={3}>
              <CardContent>
                {/* Card Header */}
                <div className={styles['card-header']}>
                  <div className={styles['card-title-section']}>
                    <Business className={styles['card-icon']} color="secondary" />
                    <div>
                      <h3 className={styles['card-title']}>{org.organizationName}</h3>
                      <p className={styles['card-subtitle']}>
                        Your Role: {org.userRole.charAt(0).toUpperCase() + org.userRole.slice(1)}
                      </p>
                    </div>
                  </div>
                  <Chip
                    label={org.subscription.status || 'Active'}
                    color={getStatusColor(org.subscription.status)}
                    size="small"
                  />
                </div>

                <Divider className={styles['card-divider']} />

                {/* Plan Details */}
                <div className={styles['plan-details']}>
                  <h4 className={styles['plan-name']}>
                    {org.subscription.planName}
                  </h4>

                  <div className={styles['detail-row']}>
                    <span className={styles['detail-label']}>Next Renewal:</span>
                    <span className={styles['detail-value']}>
                      {formatDate(org.subscription.nextRenewalDate)}
                    </span>
                  </div>

                  <div className={styles['detail-row']}>
                    <span className={styles['detail-label']}>Organization ID:</span>
                    <span className={styles['detail-value-small']}>
                      {org.organizationUuid}
                    </span>
                  </div>
                </div>

                <Divider className={styles['card-divider']} />

                {/* Features */}
                <div className={styles['features-section']}>
                  <h5 className={styles['features-title']}>Features</h5>
                  <div className={styles['features-list']}>
                    <div className={styles['feature-item']}>
                      <CheckCircle className={styles['feature-icon-check']} fontSize="small" />
                      <span>Storage: {org.subscription.features.storage}</span>
                    </div>
                    <div className={styles['feature-item']}>
                      {org.subscription.features.teamCollaboration ? (
                        <CheckCircle className={styles['feature-icon-check']} fontSize="small" />
                      ) : (
                        <Cancel className={styles['feature-icon-cancel']} fontSize="small" />
                      )}
                      <span>Team Collaboration</span>
                    </div>
                    <div className={styles['feature-item']}>
                      <CheckCircle className={styles['feature-icon-check']} fontSize="small" />
                      <span>Drones: {org.subscription.features.drones}</span>
                    </div>
                    <div className={styles['feature-item']}>
                      <CheckCircle className={styles['feature-icon-check']} fontSize="small" />
                      <span>Batteries: {org.subscription.features.batteries}</span>
                    </div>
                  </div>
                </div>

                {/* Manage Button - Only show for owners */}
                {org.userRole === 'owner' && (
                  <Button
                    variant="contained"
                    color="secondary"
                    fullWidth
                    startIcon={<Settings />}
                    onClick={() => handleManageOrganization(org.organizationUuid)}
                    disabled={processingPortal === org.organizationUuid}
                    className={styles['manage-button']}
                  >
                    {processingPortal === org.organizationUuid ? 'Opening...' : 'Manage Subscription'}
                  </Button>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* No Subscriptions */}
      {!summary.individualSubscription && summary.organizationSubscriptions.length === 0 && (
        <Alert severity="info">
          You don't have any active subscriptions. Visit our{' '}
          <a href="/plans" style={{ color: '#1976d2', textDecoration: 'underline' }}>
            plans page
          </a>{' '}
          to get started.
        </Alert>
      )}
    </Box>
  );
};

export default SubscriptionDashboard;
